import React, { useEffect, useMemo, useRef, useState } from 'react'
import { t } from 'ttag'
import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@material-ui/core'
import ExcelJS from 'exceljs'
import YumButton from '../../components/YumButton/YumButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'
import { DivisionCategoryRow, DivisionRow, UserCompanyDto, UserDto } from '../../services/api.types'
import { ImportResult } from './store/ImportUsersAdminReducer'
import { ReactComponent as UploadIcon } from '../../assets/v3/icons/announcements/upload.svg'
import { ReactComponent as DownloadIcon } from '../../assets/v3/icons/ranking/download.svg'
import { getCategoryColumns } from './utils/categoryColumnName'
import { translateDivisionCategoryType } from '../TeamManagement/TeamManagement'
import api from '../../services/api.services'
import { download } from '../../common/helpers/downloadHelpers'

export interface IImportUsersAdminProps {
  managedCompanies: UserCompanyDto[]
  uploading: boolean
  result: ImportResult | null
  selectedCompanyId: string | null
  divisionCategories: DivisionCategoryRow[]
}

export interface IImportUsersAdminDispatchProps {
  importUsers: (payload: { file: File; companyId: string; updateIfExists: boolean }) => void
  clearImportUsersResult: () => void
  setSelectedCompanyId: (payload: string) => void
  getDivisionCategories: (payload?: any) => void
}

type Props = IImportUsersAdminProps & IImportUsersAdminDispatchProps

export default function ImportUsersAdmin(props: Props) {
  const {
    managedCompanies,
    uploading,
    result,
    selectedCompanyId,
    divisionCategories,
    importUsers,
    clearImportUsersResult,
    setSelectedCompanyId,
    getDivisionCategories,
  } = props

  const [file, setFile] = useState<File | null>(null)
  const [updateIfExists, setUpdateIfExists] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const companyId = selectedCompanyId || (managedCompanies.length > 0 ? managedCompanies[0].companyId : null)

  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setSelectedCompanyId(companyId)
    }
  }, [])

  useEffect(() => {
    setFile(null)
    clearImportUsersResult()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [companyId])

  // Clear file input after upload completes
  useEffect(() => {
    if (!uploading && result) {
      setFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [uploading, result])

  useEffect(() => {
    getDivisionCategories()
  }, [])

  const companyCategories = useMemo(() => {
    if (!divisionCategories || !companyId) return []
    return divisionCategories.filter((cat) => cat.companyId === companyId)
  }, [divisionCategories, companyId])

  const dynamicColumns = useMemo(() => {
    return getCategoryColumns(companyCategories).map(({ columnName, category }) => ({
      columnName,
      displayLabel: translateDivisionCategoryType(category),
    }))
  }, [companyCategories])

  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedCompanyId(event.target.value as string)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setFile(files[0])
      clearImportUsersResult()
    }
  }

  const handleSubmit = () => {
    if (!file || !companyId || uploading) return
    importUsers({ file, companyId, updateIfExists })
  }

  const handleExportTemplate = async () => {
    if (!companyId || exportLoading) return
    setExportLoading(true)

    try {
      const [usersResponse, divisionsResponse] = await Promise.all([
        api.users.getSpecificCompanyUsers({ companyId }),
        api.users.getDivisions({ companyId }),
      ])

      const users: UserDto[] = (usersResponse as any).companyUsers || []
      const divisions: DivisionRow[] = (divisionsResponse as any).divisionDtos || []

      const categoryColumns = getCategoryColumns(companyCategories)

      const headers = [
        'email',
        'first_name',
        'last_name',
        'time_zone',
        'country',
        'team_name',
        ...categoryColumns.map((c) => c.columnName),
      ]

      const rows = users.map((user) => {
        const teamDivision = divisions.find((d) => d.id === user.divisionId && !d.categoryId)
        const teamName = teamDivision ? teamDivision.name : user.divisionName || ''

        let categoryAssignments: Record<string, string> = {}
        try {
          if (user.divisionIds && typeof user.divisionIds === 'string') {
            const parsed = JSON.parse(user.divisionIds)
            categoryAssignments = parsed.assignments || parsed || {}
          }
        } catch {
          categoryAssignments = {}
        }

        const categoryValues = categoryColumns.map(({ category }) => {
          const divisionId = categoryAssignments[category.id]
          if (!divisionId) return ''
          const division = divisions.find((d) => d.id === divisionId && d.categoryId === category.id)
          return division ? division.name : ''
        })

        return [
          user.email || '',
          user.firstName || '',
          user.lastName || '',
          user.timeZone || '',
          user.country || '',
          teamName,
          ...categoryValues,
        ]
      })

      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet('Users', { views: [{ state: 'frozen', ySplit: 1 }] })

      const headerRow = ws.addRow(headers)
      headerRow.eachCell((cell) => {
        cell.font = { bold: true }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } }
      })

      rows.forEach((row) => ws.addRow(row))

      ws.columns.forEach((column) => {
        let maxLen = 0
        column.eachCell({ includeEmpty: true }, (cell) => {
          const len = cell.value ? String(cell.value).length : 0
          if (len > maxLen) maxLen = len
        })
        column.width = Math.max(maxLen + 2, 10)
      })

      const buffer = await wb.xlsx.writeBuffer()
      const bytes = new Uint8Array(buffer as ArrayBuffer)
      let binary = ''
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      const xlsxData = btoa(binary)

      const companyName = managedCompanies.find((c) => c.companyId === companyId)?.companyName || 'company'
      const normalizedName = companyName.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')
      const date = new Date().toISOString().split('T')[0]
      const filename = `${normalizedName}_users_${date}.xlsx`

      download(filename, xlsxData, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    } catch (error) {
      console.error('Error exporting users template:', error)
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>{t`Import users`}</h1>
        {managedCompanies.length > 1 && (
          <YumSmallSelect
            variant="outlined"
            value={companyId}
            onChange={handleCompanyChange}
            inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
            className="show-on-desktop"
          >
            {managedCompanies.map((uc: UserCompanyDto, i: number) => (
              <option key={i} value={uc.companyId}>
                {uc.companyName}
              </option>
            ))}
          </YumSmallSelect>
        )}
      </YumHeader>
      {managedCompanies.length > 1 && (
        <YumSmallSelect
          variant="outlined"
          value={companyId}
          onChange={handleCompanyChange}
          inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
          className="hide-on-desktop"
          fullWidth
          style={{ marginBottom: 16 }}
        >
          {managedCompanies.map((uc: UserCompanyDto, i: number) => (
            <option key={i} value={uc.companyId}>
              {uc.companyName}
            </option>
          ))}
        </YumSmallSelect>
      )}

      <Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
        {t`Import users who are unable to use the app themselves (e.g. no smartphone). Users who can use the app should register normally using a sign-up code. Upload an Excel file (.xlsx) with one user per row. Maximum 1000 rows per import. Only the email column is required — all other columns are optional. Empty columns will not overwrite existing data.`}
      </Typography>

      <Typography variant="body2" style={{ marginBottom: 4 }}>
        <strong>{t`Columns:`}</strong>
      </Typography>
      <ul style={{ margin: '0 0 8px 0', paddingLeft: 24 }}>
        <li>email ({t`required`})</li>
        <li>first_name ({t`optional`})</li>
        <li>last_name ({t`optional`})</li>
        <li>time_zone ({t`optional, IANA format, e.g. America/New_York`})</li>
        <li>country ({t`optional, ISO 3166-1 alpha-2 code, e.g. US`})</li>
        <li>team_name ({t`optional`})</li>
        {dynamicColumns.map((col) => (
          <li key={col.columnName}>
            {col.columnName} ({t`optional`} — {col.displayLabel})
          </li>
        ))}
      </ul>

      <Box style={{ marginBottom: 24 }}>
        <YumButton
          variant="text"
          color="primary"
          size="small"
          disabled={exportLoading}
          onClick={handleExportTemplate}
          style={{ padding: '4px 8px', textTransform: 'none' }}
        >
          {exportLoading ? (
            <CircularProgress size={16} style={{ marginRight: 8 }} />
          ) : (
            <DownloadIcon style={{ marginRight: 8, width: 16, height: 16 }} />
          )}
          {t`Download current users`}
        </YumButton>
      </Box>

      <Box style={{ marginBottom: 16 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileChange}
          disabled={uploading}
          id="import-users-file"
          style={{ display: 'none' }}
        />
        <label htmlFor="import-users-file" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <YumButton variant="contained" color="primary" component="span" size="small" disabled={uploading}>
            <UploadIcon style={{ marginTop: '-2px', marginRight: '8px' }} />
            {t`Choose file`}
          </YumButton>
          {file && (
            <Typography variant="body2" style={{ color: '#666' }}>
              {file.name}
            </Typography>
          )}
        </label>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={updateIfExists}
            onChange={(e) => setUpdateIfExists(e.target.checked)}
            color="primary"
            disabled={uploading}
          />
        }
        label={t`Update data, if user already exists`}
        style={{ marginBottom: 16, display: 'block' }}
      />

      <Box>
        <YumSubmitButton
          text={t`Import`}
          color="primary"
          variant="contained"
          disabled={!file || uploading}
          submitInProgress={uploading}
          onClick={handleSubmit}
        />
      </Box>

      {result && (
        <Box
          style={{
            marginTop: 24,
            padding: 16,
            backgroundColor: '#f5f5f5',
            borderRadius: 8,
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
            {t`Import results`}
          </Typography>
          <Typography variant="body2">
            {t`Total rows`}: {result.totalRows}
          </Typography>
          <Typography variant="body2">
            {t`Created`}: {result.created}
          </Typography>
          <Typography variant="body2">
            {t`Updated`}: {result.updated}
          </Typography>
          <Typography variant="body2">
            {t`Skipped`}: {result.skipped}
          </Typography>
          {result.errors && result.errors.length > 0 && (
            <Box style={{ marginTop: 12 }}>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#d32f2f', marginBottom: 4 }}>
                {t`Errors`} ({result.errors.length}):
              </Typography>
              <Box style={{ maxHeight: 200, overflow: 'auto' }}>
                {result.errors.map((err, idx) => (
                  <Typography key={idx} variant="body2" style={{ color: '#d32f2f', fontSize: '0.8125rem' }}>
                    {t`Row`} {err.row}: {err.message}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Card>
  )
}
